import { useState } from 'react';
import {
  previewPaddleSubscription,
  validatePaddleCoupon,
} from '../helpers/billing-subscription.api';
import type {
  PaddleApplicationType,
  PaddlePreviewSubscriptionResponse,
} from '../types/paddle';
import toaster from '../../../../../shared/toaster';

const COUPON_MESSAGES = {
  PREVIEW_ERROR: 'Failed to load billing preview. Please try again.',
  INVALID_COUPON: 'Invalid coupon code. Please try again.',
} as const;

type UsePaddleCouponParams = {
  planId: number | undefined;
  paddlePriceId: string | undefined;
  application: PaddleApplicationType;
  shAccountId: number;
  onPreviewUpdate: (data: PaddlePreviewSubscriptionResponse) => void;
};

const usePaddleCoupon = ({
  planId,
  paddlePriceId,
  shAccountId,
  application,
  onPreviewUpdate,
}: UsePaddleCouponParams) => {
  const [isCouponApplying, setIsCouponApplying] = useState(false);
  const [isCouponApplied, setIsCouponApplied] = useState(false);
  const [isCouponInvalid, setIsCouponInvalid] = useState(false);
  const [preAppliedCouponCode, setPreAppliedCouponCode] = useState<string>('');

  const onApplyCoupon = async (couponCode: string) => {
    if (!couponCode) {
      setPreAppliedCouponCode('');
      setIsCouponApplied(false);
      setIsCouponInvalid(false);
      setIsCouponApplying(true);
      try {
        const response = await previewPaddleSubscription({
          accountSubscriptionPlanId: Number(planId),
          application,
          shAccountId,
        });
        onPreviewUpdate(response);
      } catch {
        toaster.error(COUPON_MESSAGES.PREVIEW_ERROR);
      } finally {
        setIsCouponApplying(false);
      }
      return;
    }

    setIsCouponInvalid(false);
    setIsCouponApplying(true);
    try {
      const { payload } = await validatePaddleCoupon({
        couponCode,
        priceId: paddlePriceId,
        planId: Number(planId),
      });

      if (!payload.isValid) {
        toaster.error(COUPON_MESSAGES.INVALID_COUPON);
        setIsCouponInvalid(true);
        return;
      }

      setPreAppliedCouponCode(couponCode);
      const response = await previewPaddleSubscription({
        accountSubscriptionPlanId: Number(planId),
        shAccountId,
        application,
        couponCode,
      });
      onPreviewUpdate(response);
      setIsCouponApplied(true);
    } catch {
      toaster.error(COUPON_MESSAGES.INVALID_COUPON);
      setIsCouponInvalid(true);
    } finally {
      setIsCouponApplying(false);
    }
  };

  const onClearCoupon = () => {
    setIsCouponInvalid(false);
  };

  const resetCouponState = () => {
    setPreAppliedCouponCode('');
    setIsCouponApplying(false);
    setIsCouponApplied(false);
    setIsCouponInvalid(false);
  };

  return {
    isCouponApplying,
    isCouponApplied,
    isCouponInvalid,
    preAppliedCouponCode,
    setPreAppliedCouponCode,
    onApplyCoupon,
    onClearCoupon,
    resetCouponState,
  };
};

export default usePaddleCoupon;
